import { ON_TIME_THRESHOLD } from 'queries/notes/helpers'
import { StoreMetrics } from '../types/storeMetrics'
import { TRANSLATION_KEYS } from '../../../locales/translation-keys'
import {
    calculatePercentage,
    formatPercentage,
    formatNumber,
} from 'utils/number-format'
import { NotesData, Sales, Payroll, Flex, Weather } from './notes'

export const parseStoreMetrics = (data?: NotesData): StoreMetrics => {
    const storeMetrics =
        data?.legacy_notes?.[0]?.store_metrics_overview ?? undefined

    return {
        ...parseSalesMetrics(storeMetrics?.sales),
        ...parsePayrollMetrics(storeMetrics?.payroll),
        ...parseFlexMetrics(storeMetrics?.flex),
        ...parseWeatherMetrics(storeMetrics?.weather),
    }
}

export const parseSalesMetrics = (salesData?: Sales) => {
    const salesValue = roundToInt(0, salesData?.sales)
    const forecastValue = roundToInt(1, salesData?.forecast)
    const salesProgress = calculatePercentage(salesValue, forecastValue)

    const compPerc = salesData?.compPerc
        ? parseFloat(salesData?.compPerc) || 0
        : 0
    const formattedCompPerc = formatPercentage(compPerc)
    const compPercStyle = compPerc <= 0 ? 'hc-clr-error' : 'hc-clr-success'

    return {
        salesValue,
        forecastValue,
        salesProgress,
        formattedCompPerc,
        compPercStyle,
    }
}

export const parsePayrollMetrics = (payrollData?: Payroll) => {
    const payrollUsed = roundToInt(0, payrollData?.total_hours)
    const payrollPlanned = roundToInt(1, payrollData?.planned_hours)
    const payrollProgress = calculatePercentage(payrollUsed, payrollPlanned)

    const flexPlan = payrollData?.better_to_poor_plan ?? 0
    const formattedFlexPlan = formatNumber(flexPlan)
    const flexPlanStyle = flexPlan <= 0 ? 'hc-clr-error' : 'hc-clr-success'

    return {
        payrollUsed,
        payrollPlanned,
        payrollProgress,
        formattedFlexPlan,
        flexPlanStyle,
    }
}

export const parseFlexMetrics = (flexData?: Flex) => {
    return {
        sfsInf: flexData?.sfs_inf?.inf_percent ?? 0,
        sfsInfColor: getStatusColor(flexData?.sfs_inf?.color_indicator),
        sfsStatus: getGoalStatus(flexData?.sfs_inf?.color_indicator),
        opuInf: flexData?.opu_inf?.inf_percent ?? 0,
        opuInfColor: getStatusColor(flexData?.opu_inf?.color_indicator),
        opuStatus: getGoalStatus(flexData?.opu_inf?.color_indicator),
        pickOnTime: flexData?.picked_on_time_percent ?? 0,
        pickOnTimeColor:
            (flexData?.picked_on_time_percent ?? ON_TIME_THRESHOLD) <
            ON_TIME_THRESHOLD
                ? 'hc-clr-error'
                : 'hc-clr-success',
        pickOnTimeStatus:
            (flexData?.picked_on_time_percent ?? ON_TIME_THRESHOLD) <
            ON_TIME_THRESHOLD
                ? TRANSLATION_KEYS.notes['below goal']
                : TRANSLATION_KEYS.notes['at goal'],
    }
}

export const parseWeatherMetrics = (weatherData?: Weather) => {
    return {
        maxTemp: roundToInt(0, weatherData?.forecast_max_temp),
        minTemp: roundToInt(0, weatherData?.forecast_min_temp),
        weatherDescription: weatherData?.forecast_description || '-',
        weatherIconUrl: weatherData?.url_forecast_icon || '-',
    }
}

export const roundToInt = (
    defaultValue: number,
    value?: string | number,
): number => {
    const parsedValue = parseFloat(String(value))
    return Math.round(
        Number.isNaN(parsedValue) || !parsedValue ? defaultValue : parsedValue,
    )
}

export const getGoalStatus = (colorIndicator: string = '#000'): string =>
    colorIndicator === '#00a200'
        ? TRANSLATION_KEYS.notes['at goal']
        : TRANSLATION_KEYS.notes['below goal']

export const getStatusColor = (colorIndicator: string = '#000'): string =>
    colorIndicator === '#00a200' ? 'hc-clr-success' : 'hc-clr-error'
